import {
  setShopifyCatalogue,
  setShopifyLinkedProducts,
  setShopifyStores,
  updateShopifyStoreInList,
} from "../slice/integrationSlice";
import { apiSlice } from "./apiSlice";

const SHOPIFY_BASE = "/integration/shopify/v1";

/**
 * Shared catalogue endpoint config: the store products list serves both the
 * Orderlink catalogue and the Shopify linked-products page.
 */
const catalogueQuery = {
  /**
   * Catalogue list for one store.
   *
   * Arg shape: ``{ storeId, limit, offset, search, listed, ordering, ... }``.
   * Always builds ``/store/{numericId}/catalogue/?…`` — never stringifies
   * the whole arg into the path (that produced ``/store/[object Object]/``).
   *
   * @param {Object} arg Query arg from the hook.
   * @return {string} Request URL.
   */
  query: arg => {
    const rawId = typeof arg === "object" && arg !== null ? (arg.storeId ?? arg.id) : arg;
    const storeId = Number(rawId);
    if (!Number.isFinite(storeId) || storeId <= 0) {
      throw new Error("Shopify catalogue requires a numeric storeId");
    }

    const params = { limit: "10", offset: "0" };
    if (typeof arg === "object" && arg !== null) {
      if (typeof arg.params === "string" && arg.params.trim()) {
        new URLSearchParams(arg.params).forEach((value, key) => {
          params[key] = value;
        });
      }
      Object.entries(arg).forEach(([key, value]) => {
        if (key === "storeId" || key === "id" || key === "params") return;
        if (value === undefined || value === null) return;
        if (typeof value === "object") return;
        params[key] = String(value);
      });
    }

    return `${SHOPIFY_BASE}/store/${storeId}/catalogue/?${new URLSearchParams(params).toString()}`;
  },
  transformResponse: payload => {
    if (Array.isArray(payload)) {
      const results = payload.map(row => ({ ...row, id: row.product_id }));
      return { count: results.length, results, next: null, previous: null };
    }
    return {
      ...payload,
      results: (payload.results || []).map(row => ({
        ...row,
        id: row.product_id,
      })),
    };
  },
  serializeQueryArgs: ({ queryArgs }) => {
    const storeId = Number(
      typeof queryArgs === "object" && queryArgs !== null
        ? (queryArgs.storeId ?? queryArgs.id)
        : queryArgs,
    );
    const entries =
      typeof queryArgs === "object" && queryArgs !== null
        ? Object.entries(queryArgs).filter(
            ([key, value]) =>
              key !== "storeId" &&
              key !== "id" &&
              key !== "params" &&
              value !== undefined &&
              value !== null &&
              typeof value !== "object",
          )
        : [];
    return `${storeId}:${new URLSearchParams(Object.fromEntries(entries)).toString()}`;
  },
  providesTags: (_result, _error, arg) => {
    const storeId = typeof arg === "object" && arg !== null ? (arg.storeId ?? arg.id) : arg;
    return [{ type: "ShopifyCatalogue", id: Number(storeId) }];
  },
};

export const shopifyApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getShopifyStores: builder.query({
      query: params => (params ? `${SHOPIFY_BASE}/store/?${params}` : `${SHOPIFY_BASE}/store/`),
      providesTags: ["ShopifyStores"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShopifyStores(result.data))).catch(_ => {});
      },
    }),
    getShopifyStoreById: builder.query({
      query: id => `${SHOPIFY_BASE}/store/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "ShopifyStores", id }],
    }),
    updateShopifyStore: builder.mutation({
      query: ({ id, data }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["ShopifyStores", "Onboarding"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateShopifyStoreInList(result.data)))
          .catch(_ => {});
      },
    }),
    deleteShopifyStore: builder.mutation({
      query: id => ({
        url: `${SHOPIFY_BASE}/store/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["ShopifyStores", "Onboarding"],
    }),
    getShopifyLocations: builder.query({
      query: id => `${SHOPIFY_BASE}/store/${id}/locations/`,
      providesTags: (_result, _error, id) => [{ type: "ShopifyLocations", id }],
    }),
    getShopifyFacilityUsage: builder.query({
      query: id => `${SHOPIFY_BASE}/store/${id}/facility_usage/`,
      providesTags: (_result, _error, id) => [{ type: "ShopifyFacilityUsage", id }],
    }),
    saveShopifyWarehouseMappings: builder.mutation({
      query: ({ id, data }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/warehouse_mappings/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ShopifyLocations", id },
        { type: "ShopifyFacilityUsage", id },
        { type: "ShopifyStores", id },
        "Onboarding",
      ],
    }),
    getShopifyTaxMappings: builder.query({
      query: id => `${SHOPIFY_BASE}/store/${id}/tax_mappings/`,
      providesTags: (_result, _error, id) => [{ type: "ShopifyStores", id }],
    }),
    saveShopifyTaxMappings: builder.mutation({
      query: ({ id, data }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/tax_mappings/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ShopifyStores", id }, "Onboarding"],
    }),
    getShopifyPaymentMappings: builder.query({
      query: id => `${SHOPIFY_BASE}/store/${id}/payment_mappings/`,
      providesTags: (_result, _error, id) => [{ type: "ShopifyStores", id }],
    }),
    saveShopifyPaymentMappings: builder.mutation({
      query: ({ id, data }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/payment_mappings/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ShopifyStores", id }, "Onboarding"],
    }),
    getShopifyCatalogue: builder.query({
      ...catalogueQuery,
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setShopifyCatalogue(result.data))).catch(_ => {});
      },
    }),
    getShopifyLinkedProducts: builder.query({
      ...catalogueQuery,
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setShopifyLinkedProducts(result.data)))
          .catch(_ => {});
      },
    }),
    listShopifyCatalogueProducts: builder.mutation({
      query: ({ id, product_ids }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/catalogue/list/`,
        method: "POST",
        body: { product_ids },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ShopifyCatalogue", id }, "Onboarding"],
    }),
    unlistShopifyCatalogueProducts: builder.mutation({
      query: ({ id, product_ids }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/catalogue/unlist/`,
        method: "POST",
        body: { product_ids },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ShopifyCatalogue", id }, "Onboarding"],
    }),
    assignShopifyCataloguePricelist: builder.mutation({
      query: ({ id, product_ids, pricelist_id }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/catalogue/assign_pricelist/`,
        method: "POST",
        body: { product_ids, pricelist_id },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ShopifyCatalogue", id }],
    }),
    assignShopifyCatalogueLocations: builder.mutation({
      query: ({ id, product_ids, warehouse_ids }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/catalogue/assign_locations/`,
        method: "POST",
        body: { product_ids, warehouse_ids },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ShopifyCatalogue", id }],
    }),
    assignShopifyCatalogueBackorder: builder.mutation({
      query: ({ id, product_ids, allow_backorder }) => ({
        url: `${SHOPIFY_BASE}/store/${id}/catalogue/allow_backorder/`,
        method: "POST",
        body: { product_ids, allow_backorder },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "ShopifyCatalogue", id }],
    }),
    getShopifyWarehouseMappings: builder.query({
      query: id => `${SHOPIFY_BASE}/store/${id}/warehouse_mappings/`,
      providesTags: (_result, _error, id) => [{ type: "ShopifyLocations", id }],
    }),
    syncShopifyInventory: builder.mutation({
      query: id => ({
        url: `${SHOPIFY_BASE}/store/${id}/sync_inventory/`,
        method: "POST",
      }),
    }),
    syncFromShopify: builder.mutation({
      query: id => ({
        url: `${SHOPIFY_BASE}/store/${id}/sync_from_shopify/`,
        method: "POST",
      }),
    }),
    getShopifyInstallClaim: builder.query({
      query: claimId => `${SHOPIFY_BASE}/install/claim/${claimId}/`,
    }),
    linkShopifyInstallClaim: builder.mutation({
      query: claimId => ({
        url: `${SHOPIFY_BASE}/install/claim/${claimId}/`,
        method: "POST",
      }),
      invalidatesTags: ["ShopifyStores", "Onboarding"],
    }),
  }),
});

export const {
  useGetShopifyStoresQuery,
  useGetShopifyStoreByIdQuery,
  useUpdateShopifyStoreMutation,
  useDeleteShopifyStoreMutation,
  useGetShopifyLocationsQuery,
  useGetShopifyFacilityUsageQuery,
  useSaveShopifyWarehouseMappingsMutation,
  useGetShopifyTaxMappingsQuery,
  useSaveShopifyTaxMappingsMutation,
  useGetShopifyPaymentMappingsQuery,
  useSaveShopifyPaymentMappingsMutation,
  useGetShopifyCatalogueQuery,
  useGetShopifyLinkedProductsQuery,
  useListShopifyCatalogueProductsMutation,
  useUnlistShopifyCatalogueProductsMutation,
  useAssignShopifyCataloguePricelistMutation,
  useAssignShopifyCatalogueLocationsMutation,
  useAssignShopifyCatalogueBackorderMutation,
  useGetShopifyWarehouseMappingsQuery,
  useSyncShopifyInventoryMutation,
  useSyncFromShopifyMutation,
  useGetShopifyInstallClaimQuery,
  useLinkShopifyInstallClaimMutation,
} = shopifyApiSlice;
